import { Navigate, type RouteObject } from 'react-router';
import { RequireAuth, RequirePermission } from '@/features/auth/guards';
import { RefreshSessionOnNavigation } from '@/features/auth/RefreshSessionOnNavigation';
import { PAGES, type PageId } from '@/features/navigation/pages';
import { AppShell, type RouteHandle } from '@/layouts/AppShell';
import { IdleLock } from '@/layouts/shell/IdleLock';
import { DashboardPage } from '@/pages/DashboardPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { PlaceholderPage } from '@/pages/PlaceholderPage';
import { ConsultationPage } from '@/pages/clinical/ConsultationPage';
import { ReferralLetterPage } from '@/pages/clinical/ReferralLetterPage';
import { MyPatientsPage } from '@/pages/clinical/MyPatientsPage';
import { MySchedulePage } from '@/pages/clinical/MySchedulePage';
import { ManagerSchedulesPage } from '@/pages/clinical/ManagerSchedulesPage';
import { ResultsToReviewPage } from '@/pages/clinical/ResultsToReviewPage';
import { ChronicCarePage } from '@/pages/clinical/ChronicCarePage';
import { ClinicalAreasPage } from '@/pages/clinical/ClinicalAreasPage';
import { MothersChildrenPage } from '@/pages/clinical/MothersChildrenPage';
import { SafetyPage } from '@/pages/clinical/SafetyPage';
import { TeleconsultsPage } from '@/pages/clinical/TeleconsultsPage';
import { VideoConsultsPage } from '@/pages/video-consults/VideoConsultsPage';
import { BillingPage } from '@/pages/billing/BillingPage';
import { CashDrawerPage } from '@/pages/billing/CashDrawerPage';
import { DispensaryPage } from '@/pages/dispensary/DispensaryPage';
import { MedicineLeafletsPage } from '@/pages/dispensary/MedicineLeafletsPage';
import { StockPage } from '@/pages/dispensary/StockPage';
import { ControlledDrugsPage } from '@/pages/dispensary/ControlledDrugsPage';
import { FinanceWorkflowsPage } from '@/pages/finance/FinanceWorkflowsPage';
import { OnlinePaymentsPage } from '@/pages/finance/OnlinePaymentsPage';
import { ApprovalsPage } from '@/pages/governance/ApprovalsPage';
import { AuditLogPage } from '@/pages/governance/AuditLogPage';
import { LabOrdersPage } from '@/pages/lab/LabOrdersPage';
import { AdminOperationsPage } from '@/pages/ops/AdminOperationsPage';
import { StaffSessionsPage } from '@/pages/ops/StaffSessionsPage';
import { HelpPage } from '@/pages/shared/HelpPage';
import { TeamMessagesPage } from '@/pages/shared/TeamMessagesPage';
import { PatientRecordPage } from '@/pages/patients/PatientRecordPage';
import { CalendarPage } from '@/pages/calendar/CalendarPage';
import { QueuePage } from '@/pages/queue/QueuePage';
import { WaitingRoomPage } from '@/pages/queue/WaitingRoomPage';
import { PatientsPage } from '@/pages/patients/PatientsPage';
import { PatientChatPage } from '@/pages/clinic-chat/PatientChatPage';
import { DeletedAppAccountsPage } from '@/pages/patients/DeletedAppAccountsPage';
import { RegisterPatientPage } from '@/pages/patients/RegisterPatientPage';
import { RouteErrorPage } from '@/pages/RouteErrorPage';
import { ServicesPage } from '@/pages/setup/ServicesPage';
import { ConsentTemplatesPage } from '@/pages/setup/ConsentTemplatesPage';
import { SignInPage } from '@/pages/SignInPage';
import { AcceptInvitePage } from '@/pages/verify/AcceptInvitePage';
import {
  StaffForgotPasswordPage,
  StaffResetPasswordPage,
} from '@/pages/verify/StaffPasswordResetPages';
import { ResetPasswordPage } from '@/pages/public/ResetPasswordPage';
import { VerifyPage } from '@/pages/verify/VerifyPage';
import { SharePage } from '@/pages/share/SharePage';
import { WellbeingPage } from '@/pages/wellbeing/WellbeingPage';
import { WellbeingSetupPage } from '@/pages/wellbeing/WellbeingSetupPage';
import { MedicalCertificatesPage } from '@/pages/ops/MedicalCertificatesPage';
import { GovernmentSchemesPage } from '@/pages/ops/GovernmentSchemesPage';
import { AttendancePage } from '@/pages/attendance/AttendancePage';
import { ManagerAttendancePage } from '@/pages/attendance/ManagerAttendancePage';
import { LocumManagementPage } from '@/pages/locum/LocumManagementPage';
import { MyContractPage } from '@/pages/locum/MyContractPage';
import { ManagerLocumPayPage } from '@/pages/locum-pay/ManagerLocumPayPage';
import { FinanceLocumPayoutPage } from '@/pages/locum-pay/FinanceLocumPayoutPage';
import { MyLocumPayPage } from '@/pages/locum-pay/MyLocumPayPage';
import { FinanceByPatientPage } from '@/pages/panel-history/FinanceByPatientPage';
import { PanelLimitConfigurationPage } from '@/pages/panel-limits/PanelLimitConfigurationPage';
import { PanelReceivablesPage } from '@/pages/panel-receivables/PanelReceivablesPage';
import { RevenuePage } from '@/pages/revenue/RevenuePage';

/**
 * One route per Admin v2 page (shared pages exist once), each gated by the
 * page's API capability for the active role. Pages render a placeholder until built.
 */
/** Pages that have a real screen; the rest render PlaceholderPage. */
const BUILT: Partial<Record<PageId, () => React.ReactElement>> = {
  patients: () => <PatientsPage />,
  registerPatient: () => <RegisterPatientPage />,
  queue: () => <QueuePage />,
  calendar: () => <CalendarPage />,
  waitingRoom: () => <WaitingRoomPage />,
  myPatients: () => <MyPatientsPage />,
  consultation: () => <ConsultationPage />,
  referralLetter: () => <ReferralLetterPage />,
  mySchedule: () => <MySchedulePage />,
  managerSchedules: () => <ManagerSchedulesPage />,
  attendance: () => <AttendancePage />,
  myContract: () => <MyContractPage />,
  myLocumPay: () => <MyLocumPayPage />,
  myRevenue: () => <RevenuePage mode="own" />,
  inbox: () => <ClinicalAreasPage mode="inbox" />,
  doctorsInbox: () => <ClinicalAreasPage mode="inbox" />,
  teleconsults: () => <TeleconsultsPage />,
  videoConsults: () => <VideoConsultsPage />,
  resultsToReview: () => <ResultsToReviewPage />,
  chronicCare: () => <ChronicCarePage />,
  mothersChildren: () => <MothersChildrenPage />,
  safety: () => <SafetyPage />,
  labOrders: () => <LabOrdersPage />,
  labResults: () => <LabOrdersPage mode="results" />,
  prescriptions: () => <DispensaryPage />,
  stock: () => <StockPage />,
  medicineLeaflets: () => <MedicineLeafletsPage />,
  controlledDrugs: () => <ControlledDrugsPage />,
  billing: () => <BillingPage />,
  governmentSchemes: () => <GovernmentSchemesPage />,
  cashDrawer: () => <CashDrawerPage />,
  medicalCertificates: () => <MedicalCertificatesPage />,
  complaints: () => <ClinicalAreasPage mode="complaints" />,
  messages: () => <ClinicalAreasPage mode="messages" />,
  medicals: () => <ClinicalAreasPage mode="medicals" />,
  panels: () => <FinanceWorkflowsPage mode="panels" />,
  panelLimits: () => <PanelLimitConfigurationPage />,
  panelUsage: () => <FinanceByPatientPage />,
  panelReceivables: () => <PanelReceivablesPage />,
  onlinePayments: () => <OnlinePaymentsPage />,
  claims: () => <FinanceWorkflowsPage mode="claims" />,
  monthlyInvoices: () => <FinanceWorkflowsPage mode="invoices" />,
  ageing: () => <FinanceWorkflowsPage mode="ageing" />,
  remittances: () => <FinanceWorkflowsPage mode="remittances" />,
  knockOff: () => <FinanceWorkflowsPage mode="knockOff" />,
  memberVerification: () => <FinanceWorkflowsPage mode="memberVerification" />,
  books: () => <AdminOperationsPage mode="books" />,
  doctorRevenue: () => <RevenuePage mode="managed" />,
  reports: () => <AdminOperationsPage mode="reports" />,
  branches: () => <AdminOperationsPage mode="branches" />,
  services: () => <ServicesPage />,
  consentTemplates: () => <ConsentTemplatesPage />,
  rosters: () => <AdminOperationsPage mode="rosters" />,
  managerAttendance: () => <ManagerAttendancePage />,
  locums: () => <LocumManagementPage />,
  managerLocumPay: () => <ManagerLocumPayPage />,
  financeLocumPay: () => <FinanceLocumPayoutPage />,
  // RL-1805 (decision D4): the Super admin approves when no manager is there.
  superAdminLocumPay: () => <ManagerLocumPayPage />,
  discounts: () => <AdminOperationsPage mode="discounts" />,
  approvals: () => <ApprovalsPage />,
  auditLog: () => <AuditLogPage />,
  compliance: () => <AdminOperationsPage mode="compliance" />,
  dataSecurity: () => <AdminOperationsPage mode="dataSecurity" />,
  staff: () => <AdminOperationsPage mode="staff" />,
  staffSessions: () => <StaffSessionsPage />,
  permissions: () => <AdminOperationsPage mode="permissions" />,
  integrations: () => <AdminOperationsPage mode="integrations" />,
  groupReports: () => <AdminOperationsPage mode="groupReports" />,
  goLive: () => <AdminOperationsPage mode="goLive" />,
  sessions: () => <WellbeingPage mode="sessions" />,
  compartmentAccess: () => <WellbeingPage mode="access" />,
  wellbeingSetup: () => <WellbeingSetupPage />,
  patientChat: () => <PatientChatPage />,
  teamMessages: () => <TeamMessagesPage />,
  help: () => <HelpPage />,
};

function pageElement(id: PageId) {
  const content = BUILT[id]?.() ?? <PlaceholderPage pageId={id} />;
  return PAGES[id].permission ? (
    <RequirePermission permission={PAGES[id].permission} pageId={id}>
      {content}
    </RequirePermission>
  ) : (
    content
  );
}

const pageRoutes: RouteObject[] = (Object.keys(PAGES) as PageId[])
  .filter((id) => id !== 'dashboard' && PAGES[id].layout !== 'full-screen')
  .map((id) => ({
    path: PAGES[id].path.slice(1),
    handle: { title: PAGES[id].label } satisfies RouteHandle,
    element: pageElement(id),
  }));

const fullScreenPageRoutes: RouteObject[] = (Object.keys(PAGES) as PageId[])
  .filter((id) => PAGES[id].layout === 'full-screen')
  .map((id) => ({
    path: PAGES[id].path,
    handle: { title: PAGES[id].label } satisfies RouteHandle,
    element: <IdleLock>{pageElement(id)}</IdleLock>,
    errorElement: <RouteErrorPage />,
  }));

const publicRoutes: RouteObject[] = [
  {
    path: '/sign-in',
    element: <SignInPage />,
    errorElement: <RouteErrorPage />,
  },
  // Public QR check printed on each MC: no sign-in.
  {
    path: '/verify/:code',
    element: <VerifyPage />,
    errorElement: <RouteErrorPage />,
  },
  // RL-1911: the outside doctor opens a record the patient shared (no sign-in).
  {
    path: '/share',
    element: <SharePage />,
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/share/:token',
    element: <SharePage />,
    errorElement: <RouteErrorPage />,
  },
  // Staff invitation link (RL-1034): public, token in the URL fragment.
  {
    path: '/accept-invite',
    element: <AcceptInvitePage />,
    errorElement: <RouteErrorPage />,
  },
  // RL-1788: staff "Forgot password?" and the emailed reset link (token in the fragment).
  {
    path: '/forgot-password',
    element: <StaffForgotPasswordPage />,
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/reset-password',
    element: <StaffResetPasswordPage />,
    errorElement: <RouteErrorPage />,
  },
  // Patient password reset from the email link (RL-1428): public, one-time token in ?token=.
  {
    path: '/app/reset-password',
    element: <ResetPasswordPage />,
    errorElement: <RouteErrorPage />,
  },
];

export const routes: RouteObject[] = [
  ...publicRoutes,
  {
    element: (
      <RequireAuth>
        <RefreshSessionOnNavigation />
      </RequireAuth>
    ),
    errorElement: <RouteErrorPage />,
    children: [
      ...fullScreenPageRoutes,
      {
        path: '/',
        element: <AppShell />,
        errorElement: <RouteErrorPage />,
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },
          { path: 'dashboard', element: <DashboardPage /> },
          ...pageRoutes,
          // RL-1886: Doctor schedules is now Weekly hours.
          {
            path: 'setup/doctor-schedules',
            element: <Navigate to="/setup/weekly-hours" replace />,
          },
          {
            // RL-1257: reached from Patients (not a sidebar page in Admin v2).
            path: 'patients/deleted-app-accounts',
            handle: { title: 'Deleted app accounts' } satisfies RouteHandle,
            element: (
              <RequirePermission permission="patients:read">
                <DeletedAppAccountsPage />
              </RequirePermission>
            ),
          },
          {
            path: 'patients/:id',
            handle: { title: 'Patient record' } satisfies RouteHandle,
            element: (
              <RequirePermission permission="patients:read">
                <PatientRecordPage />
              </RequirePermission>
            ),
          },
          {
            path: '*',
            handle: { title: 'Not found' } satisfies RouteHandle,
            element: <NotFoundPage />,
          },
        ],
      },
    ],
  },
];
